import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { usePost } from '../API';
import { ThreeColumnPage } from '../Components/ThreeColumnPage';
import { useAsyncEffect } from '../Hooks/AsyncEffect';
import { NavigationNode } from '@aidkitorg/types/lib/translation/permissions';
import { useLocalizedStrings } from '../Localization';
import { Organization } from '@aidkitorg/aidkit/lib/program/admin/organization';
import { Card, Button, DataTable, Page, Heading, Input } from '@aidkitorg/component-library';
import { useDebouncedCallback } from '../Hooks/Debounce';

function OrganizationList() {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const getOrgs = usePost('/organization/list');
  const L = useLocalizedStrings();
  const [organization, setOrganization] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  useAsyncEffect(async () => {
    const listOrgsResponse = await getOrgs({}); 
    if (Array.isArray(listOrgsResponse)) {
      setOrgs(listOrgsResponse);
    }
  }, []);

  const fetchOrgs = async (org: string, searchVal: string) => {
    const listOrgsResponse = await getOrgs({
      search: searchVal,
    });
    if (Array.isArray(listOrgsResponse)) {
      setOrgs(listOrgsResponse);
    }
  };

  const debouncedFetchOrgs = useDebouncedCallback(fetchOrgs, 300, []);

  useEffect(() => {
    debouncedFetchOrgs(organization, search);
  }, [organization, search]);

  const columns = useMemo(() => [
    {
      header: L.user.name,
      accessorKey: 'name',
    },
    {
      header: L.user.actions,
      accessorKey: 'actions',
      cell: ({ row }: any) => (
        <Button size="sm" variant="link" href={`/org/${row.original.uid}`} openInNewWindow>{L.organization.view_org_details}</Button>
      ),
    }
  ], [orgs, L]);

  return (
    <div className="pt-4">
      <div className="mb-4">
        <Heading>{L.organization.organization_management}</Heading>
        <Heading level='h4'>{L.organization.organization_management_description}</Heading>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 items-end mb-4 w-full">
        <div className="w-full max-w-sm">
          <Input
            label={L.user.search}
            placeholder={L.user.search}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="w-full max-w-sm">
          <Button variant="secondary"   
            onClick={() => window.open('/org/new', '_blank', 'noopener,noreferrer')}
          >{L.organization.create_new_organization}</Button>
        </div>
      </div>
      <Card className="w-full"> 
        <DataTable
          pageSize={100}
          columns={columns}
          data={orgs}
          getRowId={(row: Organization, index: number) => {
            return row.uid ?? String(index);
          }}
        />
      </Card>
    </div>
  );
}

export default function OrganizationManagementPage() {
  const [nav, setNav] = useState<NavigationNode[]>([]);
  const getNav = usePost("/navigation");

  useAsyncEffect(async () => {
    const result = await getNav({});
    if (Array.isArray(result?.navigation)) {
      setNav(result.navigation);
    }
  }, []);
  
  return (
    <ThreeColumnPage
      nav={nav}
      isDashboard={true}
      title="Organization Management"
      main={<OrganizationList />}
    />
  );
}
